import { safeRuleLabel } from "../fixture-placement-rules.js";
import { EXTENSION_POINT_ID_PATTERN } from "./extension-points.js";
import { readExtensionPointRows, readPointDeclaration, readSurfaceRows } from "./surface-rows.js";
import { createAssignabilityChecker } from "./surface-assignability.js";
//#region src/surfaces/check-extension-points.ts
/**
* The publish rules of surface extension points: the `extensionPoints[]` rows an app declares,
* the point lists on its surface rows, and its `uses[].point` rows (surface extension points, D2,
* D4, D5, D7, D8, D9).
*
* The JSON Schema and the wire twin check each field alone. This module checks what needs two
* fields, two rows or the registry: the owner prefix, a duplicate id, `fill` on the right kind, a
* list on the right row kind, a reference that names no point or a point of the wrong kind, a
* point that changed its schema under its id, and the fit of every implementer and every point use
* against the point. The fit is `findAssignabilityMismatch`, through one memoised checker per call.
* It follows the shape of `check-surface-uses.ts`: pure, every access guarded, every issue an
* error, app-authored strings in a message only through `safeRuleLabel`.
*/
/** The deepest nesting `sameSchema` compares; a deeper value reads as changed (refuse over accept, F3). */
const CANONICAL_MAX_DEPTH = 64;
const POINT_ID = new RegExp(EXTENSION_POINT_ID_PATTERN);
function issue(rule, path, message) {
	return {
		severity: "error",
		path,
		rule,
		message
	};
}
/** True for a string of at most `EXTENSION_POINT_ID_MAX_LENGTH` characters that matches the pattern; the length is checked first. */
function isPointId(value) {
	return typeof value === "string" && value.length <= 96 && POINT_ID.test(value);
}
/** The owner part of a valid point id: the text before its one `/`. */
function ownerOf(id) {
	return id.slice(0, id.indexOf("/"));
}
function quoted(value) {
	return `'${safeRuleLabel(value)}'`;
}
/** The row's name for a message: its sanitised `name`, or its pointer when the name is not a string. */
function label(row) {
	return row.name === null ? row.pointer : `surface ${quoted(row.name)}`;
}
function invalidIdIssue(path, value, where) {
	return issue("extension-point-id-invalid", path, `${where} ${quoted(value)}, which is not an extension point id '<owner>/<name>.vN' of at most 96 characters (D4).`);
}
/**
* A key-order-free text of a JSON value, or `null` when the value nests deeper than
* `CANONICAL_MAX_DEPTH` or holds a value JSON cannot carry. Object keys are sorted by code unit;
* array order is kept.
*/
function canonical(value, depth) {
	if (depth > CANONICAL_MAX_DEPTH) return null;
	if (value === null || value === void 0) return String(value);
	if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") return JSON.stringify(value);
	if (typeof value !== "object") return null;
	const parts = [];
	if (Array.isArray(value)) {
		for (const member of value) {
			const text = canonical(member, depth + 1);
			if (text === null) return null;
			parts.push(text);
		}
		return `[${parts.join(",")}]`;
	}
	const record = value;
	for (const key of Object.keys(record).sort()) {
		const text = canonical(record[key], depth + 1);
		if (text === null) return null;
		parts.push(`${JSON.stringify(key)}:${text}`);
	}
	return `{${parts.join(",")}}`;
}
/** The part of a declaration D5 freezes: `kind`, `fill`, `input` and `outputs`. `description` and `deprecated` may change. */
function frozenPart(row) {
	return {
		kind: row.kind,
		fill: row.hasFill ? row.fill : void 0,
		input: row.hasInput ? row.input : void 0,
		outputs: Object.fromEntries(row.outputs)
	};
}
/** True when two declarations carry the same frozen part, compared structurally with key order ignored. */
function sameSchema(a, b) {
	const left = canonical(frozenPart(a), 0);
	return left !== null && left === canonical(frozenPart(b), 0);
}
/** True when an `implements` entry may name this point: a surface point, or a slot filled by a surface (D8). */
function isImplementable(point) {
	return point.kind === "surface" || point.kind === "slot" && point.fill === "surface";
}
function mismatchText(found) {
	return `${found.reason} at ${quoted(found.path)}`;
}
/**
* The resolver of point references for one call. A reference resolves, in this order:
* 1. to a row of this manifest whose id is valid and whose owner is `ctx.appSlug`;
* 2. for an id whose owner is `ctx.appSlug`: to `ctx.ownPublished`, then `ctx.points`; `unjudged`
*    when `ctx.ownPublished` is `null`;
* 3. for any other id: to `ctx.points`; `unjudged` when `ctx.points` is `null`.
* A manifest row that claims another owner's id never resolves, so it cannot shadow the registry.
* Each registry record is read once per call.
*/
function makeResolver(own, ctx) {
	const read = /* @__PURE__ */ new Map();
	const fromRegistry = (id, published) => {
		if (published === void 0) return "unknown";
		const known = read.get(id);
		if (known !== void 0) return known;
		const resolved = {
			row: readPointDeclaration(published.declaration, ""),
			source: `as first published by ${safeRuleLabel(published.owner)}@${safeRuleLabel(published.version)}`
		};
		read.set(id, resolved);
		return resolved;
	};
	return (id) => {
		const declared = own.get(id);
		if (declared !== void 0) return {
			row: declared,
			source: "declared by this version"
		};
		if (ownerOf(id) === ctx.appSlug) {
			if (ctx.ownPublished === null) return "unjudged";
			return fromRegistry(id, ctx.ownPublished.get(id) ?? ctx.points?.get(id));
		}
		if (ctx.points === null) return "unjudged";
		return fromRegistry(id, ctx.points.get(id));
	};
}
/**
* The issues of the `extensionPoints[]` rows, and the map of this app's own valid declarations
* (first row per id) that references resolve against.
*/
function declarationIssues(points, ctx, issues) {
	const own = /* @__PURE__ */ new Map();
	const seen = /* @__PURE__ */ new Set();
	for (const point of points) {
		const idPath = `${point.pointer}/id`;
		const id = point.id;
		if (!isPointId(id)) issues.push(invalidIdIssue(idPath, id, "extensionPoints[] declares the id"));
		else {
			if (seen.has(id)) issues.push(issue("extension-point-duplicate", idPath, `extensionPoints[] declares ${quoted(id)} more than once; declare each extension point once (D2).`));
			seen.add(id);
			if (ownerOf(id) !== ctx.appSlug) issues.push(issue("extension-point-owner-mismatch", idPath, `extensionPoints[] declares ${quoted(id)}, whose owner ${quoted(ownerOf(id))} is not this app ${quoted(ctx.appSlug)}; an app declares only ids under its own slug (D2).`));
			else {
				if (!own.has(id)) own.set(id, point);
				const published = ctx.ownPublished?.get(id);
				if (published !== void 0 && !sameSchema(point, readPointDeclaration(published.declaration, ""))) issues.push(issue("extension-point-changed", point.pointer, `extension point ${quoted(id)} differs from the point ${safeRuleLabel(published.owner)}@${safeRuleLabel(published.version)} first published in kind, fill, input or outputs; a point never changes under its id, so a breaking change is a new .vN (D5).`));
			}
		}
		if (point.kind === "slot" && !point.hasFill) issues.push(issue("extension-point-fill-invalid", point.pointer, `extension point ${quoted(id)} is a slot and declares no fill; a slot is filled by 'item' or 'surface' (D8).`));
		else if (point.kind === "surface" && point.hasFill) issues.push(issue("extension-point-fill-invalid", `${point.pointer}/fill`, `extension point ${quoted(id)} is a surface point and declares a fill; only a slot declares one (D8).`));
	}
	return own;
}
/** The three placement rules of one surface row: each list on the row kind that may carry it (D7, D9). */
function placementIssues(row) {
	const issues = [];
	const who = label(row);
	if (row.hasImplements && (row.claimsRoot || row.kind !== "embedded")) {
		const what = row.claimsRoot ? "claims root" : "is not embedded";
		issues.push(issue("surface-implements-not-embedded", `${row.pointer}/implements`, `${who} ${what} and declares implements; only an embedded, non-root surface implements an extension point (D9).`));
	}
	if (row.hasFills && row.claimsRoot) issues.push(issue("surface-fills-on-root", `${row.pointer}/fills`, `${who} claims root and declares fills; the root provides slots and never fills one (D7).`));
	if (row.hasSlots && !row.claimsRoot) issues.push(issue("surface-slots-not-root", `${row.pointer}/slots`, `${who} does not claim root and declares slots; only the root shell provides a slot (D7).`));
	return issues;
}
/**
* The first place this surface does not implement the point: the point passes an input and the
* surface takes none, the point's input does not fit the surface's input, the surface emits no
* output of a point output's name, or its output does not give what the point output requires.
* The point is the writer of the input and the reader of the outputs. `null` when it holds.
*/
function implementerIssue(row, id, point, path, fits) {
	const who = label(row);
	const name = `point ${quoted(id)} (${point.source})`;
	if (point.row.hasInput && !row.hasInput) return issue("surface-implements-input-unsatisfied", path, `${who} takes no input, and ${name} passes one (D9).`);
	const input = fits(point.row.input, row.input);
	if (input !== null) return issue("surface-implements-input-unsatisfied", path, `${who} input does not accept what ${name} passes: ${mismatchText(input)} (D9).`);
	for (const [output, reader] of point.row.outputs) {
		if (!row.outputs.has(output)) return issue("surface-implements-output-unsatisfied", path, `${who} emits no output ${quoted(output)}, which ${name} declares (D9).`);
		const found = fits(row.outputs.get(output), reader);
		if (found !== null) return issue("surface-implements-output-unsatisfied", path, `${who} output ${quoted(output)} does not give what ${name} declares: ${mismatchText(found)} (D9).`);
	}
	return null;
}
/** The issues of one point list of one surface row, entry by entry: at most one issue per entry. */
function listIssues(row, list, resolve, fits) {
	const issues = [];
	const seen = /* @__PURE__ */ new Set();
	row[list].forEach((entry, j) => {
		const path = `${row.pointer}/${list}/${j}`;
		if (!isPointId(entry)) {
			issues.push(invalidIdIssue(path, entry, `${label(row)} lists in ${list}`));
			return;
		}
		if (seen.has(entry)) {
			issues.push(issue("extension-point-duplicate", path, `${label(row)} lists ${quoted(entry)} in ${list} more than once; list each extension point once (D7).`));
			return;
		}
		seen.add(entry);
		const found = resolve(entry);
		if (found === "unjudged") return;
		if (found === "unknown") {
			issues.push(issue("extension-point-unknown", path, `${label(row)} lists ${quoted(entry)} in ${list}, and no published extension point has that id (D2).`));
			return;
		}
		if (list === "implements") {
			if (!isImplementable(found.row)) {
				issues.push(issue("extension-point-kind-mismatch", path, `${label(row)} implements ${quoted(entry)} (${found.source}), which is neither a surface point nor a slot filled by a surface (D8, D9).`));
				return;
			}
			const fault = implementerIssue(row, entry, found, path, fits);
			if (fault !== null) issues.push(fault);
			return;
		}
		if (found.row.kind !== "slot") issues.push(issue("extension-point-kind-mismatch", path, `${label(row)} lists ${quoted(entry)} (${found.source}) in ${list}, and it is not a slot (D7).`));
	});
	return issues;
}
/**
* The first place a `uses[].point` row does not hold against the point: it passes an input to a
* point that takes none, its inputs do not fit the point's input, it reads an output the point
* does not declare, or the point's output does not give what the row reads. The row is the writer
* of the input and the point is the writer of the outputs. `null` when it holds.
*/
function pointUseFault(use, id, point, fits) {
	const name = `point ${quoted(id)} (${point.source})`;
	if (use.inputs !== void 0 && !point.row.hasInput) return issue("surface-uses-point-input-unsatisfied", use.pointer, `${name} takes no input, and the uses[] row passes one (D9).`);
	const input = fits(use.inputs, point.row.input);
	if (input !== null) return issue("surface-uses-point-input-unsatisfied", use.pointer, `${name} input does not accept what the uses[] row passes: ${mismatchText(input)} (D9).`);
	for (const [output, reader] of use.outputs) {
		if (!point.row.outputs.has(output)) return issue("surface-uses-point-output-unsatisfied", use.pointer, `${name} declares no output ${quoted(output)}, which the uses[] row reads (D9).`);
		const found = fits(point.row.outputs.get(output), reader);
		if (found !== null) return issue("surface-uses-point-output-unsatisfied", use.pointer, `${name} output ${quoted(output)} does not give what the uses[] row reads: ${mismatchText(found)} (D9).`);
	}
	return null;
}
/**
* The issues of the `uses[]` rows of one surface that carry a `point` key: at most one issue per row.
* The self embed and the cycle are `checkSurfaceUses`'s.
*/
function pointUseIssues(row, resolve, fits) {
	const issues = [];
	const seen = /* @__PURE__ */ new Set();
	for (const use of row.uses) {
		if (!use.hasPoint) continue;
		const id = use.point;
		if (!isPointId(id)) {
			issues.push(invalidIdIssue(`${use.pointer}/point`, id, `${label(row)} names in uses[] the point`));
			continue;
		}
		const key = JSON.stringify([id, use.app]);
		if (seen.has(key)) {
			issues.push(issue("extension-point-duplicate", use.pointer, `${label(row)} lists point ${quoted(id)} with the same app in uses[] more than once; declare each use once (D9).`));
			continue;
		}
		seen.add(key);
		const found = resolve(id);
		if (found === "unjudged") continue;
		if (found === "unknown") {
			issues.push(issue("extension-point-unknown", use.pointer, `${label(row)} names point ${quoted(id)} in uses[], and no published extension point has that id (D2).`));
			continue;
		}
		if (found.row.kind !== "surface") {
			issues.push(issue("extension-point-kind-mismatch", use.pointer, `${label(row)} names ${quoted(id)} (${found.source}) in uses[], and it is not a surface point; a root mounts slot fills (D9).`));
			continue;
		}
		const fault = pointUseFault(use, id, found, fits);
		if (fault !== null) issues.push(fault);
	}
	return issues;
}
/**
* The extension point issues of an untrusted manifest (surface extension points).
*
* `ctx.appSlug` is the registered slug of the app being published, never the manifest's `appId`.
* `ctx.points` is every published point a reference may name, keyed by id; `ctx.ownPublished` is the
* points this app's earlier versions published. `zion-app validate` passes `null` for both.
*
* First, per `extensionPoints[]` row, in row order:
* - `extension-point-id-invalid` at `<point>/id`: an id that is not a string of at most 96
*   characters matching `EXTENSION_POINT_ID_PATTERN`, an absent id included;
* - `extension-point-duplicate` at `<point>/id`: a valid id an earlier row already declared;
* - `extension-point-owner-mismatch` at `<point>/id`: a valid id whose owner part is not
*   `ctx.appSlug`;
* - `extension-point-changed` at `<point>`: an owned id that `ctx.ownPublished` holds with another
*   `kind`, `fill`, `input` or `outputs`. The compare is structural: object key order is ignored,
*   array order counts, annotations inside a schema count, and `outputs: {}` equals no outputs.
*   `description` and `deprecated` take no part (D5);
* - `extension-point-fill-invalid`: a `slot` with no `fill`, at `<point>`; a `surface` point with a
*   `fill`, at `<point>/fill`. Another `kind` is Ajv's to report.
*
* Then, per surface row, in row order:
* - `surface-implements-not-embedded` at `<row>/implements`: the key on a root claim or on a row
*   whose kind is not `embedded`; `surface-fills-on-root` at `<row>/fills`: the key on a root claim;
*   `surface-slots-not-root` at `<row>/slots`: the key on a row with no root claim. Each fires on
*   the key, whatever its value.
* - Each entry of `implements`, then `fills`, then `slots`, at `<row>/<list>/<j>`, gets at most
*   one issue: `extension-point-id-invalid`; `extension-point-duplicate` for an id the list already
*   named; `extension-point-unknown` for an id with no point; `extension-point-kind-mismatch` when
*   `implements` names a point that is neither `kind: 'surface'` nor a slot with `fill: 'surface'`,
*   or `fills` or `slots` names a point that is not a slot; then, for `implements`,
*   `surface-implements-input-unsatisfied` (the point passes an input and the row takes none, or
*   the point's input does not fit the row's input) or `surface-implements-output-unsatisfied`
*   (the row lacks a point output, or its output does not fit the point output).
* - Each `uses[]` row with a `point` key gets at most one issue: `extension-point-id-invalid` at
*   `<use>/point`; then at `<use>`: `extension-point-duplicate` for a `point` and `app` pair the
*   surface already used; `extension-point-unknown`; `extension-point-kind-mismatch` for a point
*   that is not `kind: 'surface'`; `surface-uses-point-input-unsatisfied` (the row passes inputs to
*   a point with no input, or its inputs do not fit the point's input);
*   `surface-uses-point-output-unsatisfied` (the row reads an output the point does not declare,
*   or the point output does not fit what the row reads).
*
* A point row whose mount lands on its own surface, or closes a cycle of embeds inside this app,
* is `checkSurfaceUses`'s to refuse (`surface-uses-self`, `surface-uses-cycle`), so a cycle through
* `{app, surface}` rows and point rows is judged on one graph.
*
* A reference resolves first to this manifest's own valid declaration, so the CLI judges an app's
* own points with no registry. A reference to another owner with `ctx.points: null`, or to an own
* id this manifest does not declare with `ctx.ownPublished: null`, gets only the pure rules. A
* manifest row that declares another owner's id never resolves a reference.
*
* Every issue is `severity: 'error'`. One assignability checker serves the call and each registry
* record is read once, so the work grows with the bytes of the manifest and of the points it names.
* App-authored strings reach a message only through `safeRuleLabel`. Pure; never throws.
*/
function checkExtensionPoints(manifest, ctx) {
	const issues = [];
	const resolve = makeResolver(declarationIssues(readExtensionPointRows(manifest), ctx, issues), ctx);
	const fits = createAssignabilityChecker();
	for (const row of readSurfaceRows(manifest)) {
		issues.push(...placementIssues(row));
		for (const list of [
			"implements",
			"fills",
			"slots"
		]) issues.push(...listIssues(row, list, resolve, fits));
		issues.push(...pointUseIssues(row, resolve, fits));
	}
	return issues;
}
//#endregion
export { checkExtensionPoints };

//# sourceMappingURL=check-extension-points.js.map